import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Search, Trash2, UserPlus, X } from "lucide-react";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../ui/alert-dialog";
import { api, formatApiError } from "../../../lib/api";
import ParentDialog from "./ParentDialog";
import { prettyPhone } from "./feeUtils";

function ResultRow({ p, onPick, onRemove, i }) {
    return (
        <div className="flex items-center gap-1 rounded-lg hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-colors">
            <button type="button" onClick={() => onPick(p)} data-testid={`fee-parent-result-${i}`} className="flex-1 min-w-0 text-left px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-[#0F172A] truncate">{p.name || "Unnamed parent"}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full shrink-0 ${p.source === "saved" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        {p.source === "saved" ? "Saved" : p.source_label}
                    </span>
                </div>
                <div className="text-xs text-slate-500 truncate">{prettyPhone(p.phone)}{p.student_name ? ` · Student: ${p.student_name}` : ""}</div>
            </button>
            {p.source === "saved" && (
                <button type="button" onClick={() => onRemove(p)} aria-label="Remove from Fee Reminder list" data-testid={`fee-parent-remove-${i}`}
                    className="w-10 h-10 shrink-0 grid place-items-center rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 mr-1">
                    <Trash2 className="w-4 h-4" />
                </button>
            )}
        </div>
    );
}

export default function ParentPicker({ selected, onSelect }) {
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null); // {parent} | {parent:null}
    const [removing, setRemoving] = useState(null);
    const [reload, setReload] = useState(0);

    const confirmRemove = async () => {
        const p = removing;
        try {
            await api.delete(`/admin/fee-reminders/parents/${p.id}`);
            toast.success(`${p.name || "Parent"} removed from the Fee Reminder list`);
            if (selected?.id === p.id) onSelect(null);
            setReload((n) => n + 1);
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setRemoving(null); }
    };

    useEffect(() => {
        let alive = true;
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await api.get("/admin/fee-reminders/parents/search", { params: { q } });
                if (alive) setItems(res.data.items || []);
            } catch { if (alive) setItems([]); } finally { if (alive) setLoading(false); }
        }, 300);
        return () => { alive = false; clearTimeout(t); };
    }, [q, reload]);

    const pick = async (p) => {
        if (p.source === "saved") return onSelect(p);
        try {
            const res = await api.post("/admin/fee-reminders/parents", { name: p.name || "Parent", phone: p.phone, student_name: p.student_name });
            if (!res.data.existing) toast.success("Parent saved to your Fee Reminder list");
            onSelect(res.data.parent);
            setReload((n) => n + 1);
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    return (
        <section className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 space-y-3" data-testid="fee-parent-picker">
            <div className="flex items-center justify-between gap-2">
                <h3 className="font-heading font-bold text-[#0F172A]">1 · Search Parent</h3>
                <button type="button" onClick={() => setDialog({ parent: null })} data-testid="fee-add-parent"
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1E3A8A] hover:underline">
                    <UserPlus className="w-4 h-4" /> Add New Parent
                </button>
            </div>
            {selected ? (
                <div className="flex items-start justify-between gap-3 bg-[#EEF2FF] border border-[#C7D2FE] rounded-xl p-3" data-testid="fee-selected-parent">
                    <div className="min-w-0">
                        <div className="font-bold text-[#0F172A] truncate" data-testid="fee-selected-name">{selected.name}</div>
                        <div className="text-sm text-slate-600" data-testid="fee-selected-phone">WhatsApp: {prettyPhone(selected.phone)}</div>
                        {selected.student_name && <div className="text-xs text-slate-500">Student: {selected.student_name}</div>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                        <button type="button" onClick={() => setDialog({ parent: selected })} data-testid="fee-edit-parent" aria-label="Edit parent"
                            className="w-9 h-9 grid place-items-center rounded-lg hover:bg-white text-slate-600"><Pencil className="w-4 h-4" /></button>
                        <button type="button" onClick={() => setRemoving(selected)} data-testid="fee-remove-selected-parent" aria-label="Remove from Fee Reminder list"
                            className="w-9 h-9 grid place-items-center rounded-lg hover:bg-white text-rose-600"><Trash2 className="w-4 h-4" /></button>
                        <button type="button" onClick={() => onSelect(null)} data-testid="fee-clear-parent" aria-label="Change parent"
                            className="w-9 h-9 grid place-items-center rounded-lg hover:bg-white text-slate-600"><X className="w-4 h-4" /></button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="relative">
                        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Parent name, student name or WhatsApp number"
                            data-testid="fee-parent-search" className="w-full h-11 pl-9 pr-9 rounded-lg border border-slate-300 focus:border-[#1E3A8A] outline-none text-sm" />
                        {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />}
                    </div>
                    <div className="max-h-72 overflow-y-auto -mx-1 px-1 space-y-0.5" data-testid="fee-parent-results">
                        {items.map((p, i) => <ResultRow key={`${p.source}-${p.phone}`} p={p} i={i} onPick={pick} onRemove={setRemoving} />)}
                        {!loading && items.length === 0 && (
                            <div className="text-sm text-slate-500 py-4 text-center" data-testid="fee-parent-empty">
                                {q.trim() ? "No parent found." : "No saved parents yet."}{" "}
                                <button type="button" className="font-bold text-[#1E3A8A] hover:underline" onClick={() => setDialog({ parent: null })}>Add New Parent</button>
                            </div>
                        )}
                    </div>
                </>
            )}
            <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
                <AlertDialogContent className="z-[70]" data-testid="fee-remove-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove {removing?.name || "this parent"} from Fee Reminders?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes only the saved Fee Reminder contact ({prettyPhone(removing?.phone)}). Registrations, student profiles, payment links, bookings and all other dashboard records stay exactly as they are.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="fee-remove-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid="fee-remove-confirm" onClick={confirmRemove} className="bg-rose-600 hover:bg-rose-700">Remove</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            {dialog && (
                <ParentDialog open onOpenChange={(o) => !o && setDialog(null)} parent={dialog.parent}
                    initialName={/\d/.test(q) || items.length ? "" : q} onSaved={onSelect} />
            )}
        </section>
    );
}
